import { useRef, useState } from "react";
import { DraftEditor } from "@/components/governance-draft-editor";
import { RevisionEditor } from "@/components/governance-revision-editor";
import type { GovernanceRevision } from "@/lib/governance-revision";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { governanceErrorMessage } from "@/lib/governance-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAuth, hasPermission } from "@/lib/auth";
import {
  CheckCircle, XCircle, Clock, FileText, Archive, Send, Globe,
  History, AlertCircle, ChevronRight
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GovernanceItem {
  id: string;
  chatbotId: string;
  type: string;
  name: string;
  url?: string;
  ingestStatus: string;
  governanceStatus: string;
  governanceVersion: number;
  canEditDraft?: boolean;
  canCreateRevision?: boolean;
  createdByUserId?: string;
  submittedByUserId?: string;
  submittedAt?: string;
  reviewedByUserId?: string;
  reviewedAt?: string;
  approvedByUserId?: string;
  approvedAt?: string;
  rejectedByUserId?: string;
  rejectedAt?: string;
  reviewComments?: string;
  publishedAt?: string;
  createdAt: string;
}

interface GovernanceVersion {
  id: string;
  knowledgeSourceId: string;
  version: number;
  name: string;
  contentSnapshot?: string;
  publishedByUserId?: string;
  publisher?: string;
  canRestore?: boolean;
  restoreRestriction?: string;
  publishedAt: string;
  reviewComments?: string;
}

interface GovernanceResponse {
  total: number;
  page: number;
  pageSize: number;
  items: GovernanceItem[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  draft:     { label: "Draft",          color: "bg-gray-100 text-gray-700",   icon: <FileText className="h-3 w-3" /> },
  submitted: { label: "Pending Review", color: "bg-yellow-100 text-yellow-800", icon: <Clock className="h-3 w-3" /> },
  approved:  { label: "Approved",       color: "bg-blue-100 text-blue-800",   icon: <CheckCircle className="h-3 w-3" /> },
  rejected:  { label: "Rejected",       color: "bg-red-100 text-red-800",     icon: <XCircle className="h-3 w-3" /> },
  published: { label: "Published",      color: "bg-green-100 text-green-800", icon: <Globe className="h-3 w-3" /> },
  archived:  { label: "Archived",       color: "bg-slate-100 text-slate-600", icon: <Archive className="h-3 w-3" /> },
};

const TYPE_LABELS: Record<string, string> = {
  url:        "URL / Website",
  document:   "Document",
  text:       "Plain text",
  sharepoint: "SharePoint",
  libguides:  "LibGuides",
};

function GovernanceBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, color: "bg-gray-100 text-gray-700", icon: null };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

function fmt(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Source row ───────────────────────────────────────────────────────────────

interface SourceRowProps {
  item: GovernanceItem;
  onAction: (id: string, action: string, comment?: string) => void;
  onViewHistory: (id: string) => void;
  onCreateRevision: (id: string) => void;
  creatingRevision: boolean;
  canReview: boolean;
  canPublish: boolean;
  canManage: boolean;
}

function SourceRow({ item, onAction, onViewHistory, onCreateRevision, creatingRevision, canReview, canPublish, canManage }: SourceRowProps) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleReject = async () => {
    if (!rejectComment.trim()) return;
    setSubmitting(true);
    onAction(item.id, "reject", rejectComment.trim());
    setRejectOpen(false);
    setRejectComment("");
    setSubmitting(false);
  };

  return (
    <>
      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-medium text-sm text-gray-900 truncate">{item.name}</span>
            <GovernanceBadge status={item.governanceStatus} />
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {TYPE_LABELS[item.type] ?? item.type}
            </span>
            {item.governanceVersion > 0 && (
              <span className="text-xs text-muted-foreground" title="Latest published snapshot version">v{item.governanceVersion}</span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
            {item.url && (
              <span className="truncate max-w-xs" title={item.url}>
                {item.url}
              </span>
            )}
            <span>Created {fmt(item.createdAt)}</span>
            {item.submittedAt && <span>Submitted {fmt(item.submittedAt)}</span>}
            {item.approvedAt && <span>Approved {fmt(item.approvedAt)}</span>}
            {item.rejectedAt && <span>Rejected {fmt(item.rejectedAt)}</span>}
            {item.publishedAt && <span>Published {fmt(item.publishedAt)}</span>}
          </div>

          {item.reviewComments && item.governanceStatus === "rejected" && (
            <div className="mt-2 flex items-start gap-1.5 rounded bg-red-50 border border-red-100 px-2 py-1.5 text-xs text-red-700">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>{item.reviewComments}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 shrink-0">
          {canManage && item.canCreateRevision && item.governanceStatus === "published" && (
            <Button size="sm" variant="outline" disabled={creatingRevision} onClick={() => onCreateRevision(item.id)}>
              Create new revision
            </Button>
          )}
          {canManage && item.canEditDraft && <Button asChild size="sm" variant="outline">
            <a href={`/content-governance?draft=${encodeURIComponent(item.id)}`}>Edit draft</a>
          </Button>}
          {/* Submit for review — creator */}
          {canManage && (item.governanceStatus === "draft" || item.governanceStatus === "rejected") &&
            item.ingestStatus === "processed" && (
            <Button size="sm" variant="outline" onClick={() => onAction(item.id, "submit")}>
              <Send className="h-3 w-3 mr-1" />
              Submit
            </Button>
          )}

          {/* Approve — reviewer */}
          {canReview && item.governanceStatus === "submitted" && (
            <Button size="sm" variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={() => onAction(item.id, "approve")}>
              <CheckCircle className="h-3 w-3 mr-1" />
              Approve
            </Button>
          )}

          {/* Reject — reviewer */}
          {canReview && (item.governanceStatus === "submitted" || item.governanceStatus === "approved") && (
            <Button size="sm" variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => setRejectOpen(true)}>
              <XCircle className="h-3 w-3 mr-1" />
              Reject
            </Button>
          )}

          {/* Publish — publisher */}
          {canPublish && item.governanceStatus === "approved" && (
            <Button size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => onAction(item.id, "publish")}>
              <Globe className="h-3 w-3 mr-1" />
              Publish
            </Button>
          )}

          {/* Archive — publisher */}
          {canPublish && item.governanceStatus !== "archived" && (
            <Button size="sm" variant="ghost"
              className="text-gray-500 hover:text-gray-700"
              onClick={() => onAction(item.id, "archive")}>
              <Archive className="h-3 w-3 mr-1" />
              Archive
            </Button>
          )}

          {/* Version history */}
          {item.governanceVersion > 0 && (
            <Button size="sm" variant="outline"
              aria-label="Open published version history"
              onClick={() => onViewHistory(item.id)}>
              <History className="h-3 w-3 mr-1" />
              History
            </Button>
          )}
        </div>
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Content</DialogTitle>
            <DialogDescription>
              Provide a mandatory comment explaining why this content is being rejected.
              The creator will see this feedback.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              placeholder="Enter rejection reason (required)…"
              value={rejectComment}
              onChange={e => setRejectComment(e.target.value)}
              rows={4}
              className="resize-none"
            />
            {!rejectComment.trim() && (
              <p className="mt-1 text-xs text-red-600">A comment is required to reject content.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectOpen(false); setRejectComment(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={!rejectComment.trim() || submitting}
              className="bg-red-600 hover:bg-red-700 text-white">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Version history panel ────────────────────────────────────────────────────

function VersionHistoryPanel({ sourceId, onClose }: { sourceId: string; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [restoreError, setRestoreError] = useState("");
  const [restoredId, setRestoredId] = useState<string>();
  const [confirmVersion, setConfirmVersion] = useState<string>();
  const submitting = useRef(false);
  const restore = useMutation({
    mutationFn: async (versionId: string) => (await apiRequest("POST", `/api/content-governance/${sourceId}/versions/${versionId}/restore`)).json(),
    onSuccess: (revision: GovernanceRevision) => {
      qc.invalidateQueries({ queryKey: ["content-governance"] });
      qc.invalidateQueries({ queryKey: ["content-governance-summary"] });
      qc.invalidateQueries({ queryKey: ["content-governance-revisions"] });
      qc.invalidateQueries({ queryKey: ["content-governance-revision", revision.id] });
      qc.invalidateQueries({ queryKey: ["content-governance-versions", sourceId] });
      setRestoredId(revision.id);
      setConfirmVersion(undefined);
    },
    onError: (error: unknown) => setRestoreError(governanceErrorMessage(error)),
    onSettled: () => { submitting.current = false; },
  });
  const { data: versions, isLoading, error: historyError } = useQuery<GovernanceVersion[]>({
    queryKey: ["content-governance-versions", sourceId],
    queryFn: () => apiRequest("GET", `/api/content-governance/${sourceId}/versions`).then(r => r.json()),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Version History
          </DialogTitle>
          <DialogDescription>
            Immutable snapshots created each time this content was published.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 overflow-y-auto space-y-2 py-1">
          {isLoading && <p className="text-sm text-gray-500 text-center py-4">Loading…</p>}
          {historyError && <p role="alert">Unable to load version history. Please close and try again.</p>}
          {restoredId && <div role="status" className="rounded border p-3">
            Version restored into a same-source revision draft. Published content was not changed.
            <a className="block underline mt-2" href={`/content-governance?revision=${encodeURIComponent(restoredId)}`}>
              Open restored revision to edit and submit for review
            </a>
          </div>}
          {!isLoading && !historyError && (!versions || versions.length === 0) && (
            <p className="text-sm text-gray-500 text-center py-4">No publish history yet.</p>
          )}
          {versions?.map(v => (
            <div key={v.id} className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm text-gray-900">v{v.version} — {v.name}</span>
                <span className="text-xs text-gray-500">{fmt(v.publishedAt)}</span>
              </div>
              <p className="text-xs text-gray-500">Published by {v.publisher || v.publishedByUserId || "Unknown publisher"}</p>
              {v.reviewComments && (
                <p className="text-xs text-gray-500 mt-1 italic">"{v.reviewComments}"</p>
              )}
              {v.restoreRestriction && <p className="text-sm text-muted-foreground">{v.restoreRestriction}</p>}
              {hasPermission(user, "knowledge.manage") && v.canRestore && <Button variant="outline" size="sm"
                disabled={restore.isPending || !!restoredId} onClick={() => {
                  setRestoreError(""); setConfirmVersion(v.id);
                }}>Restore into revision draft</Button>}
              {v.contentSnapshot && (
                <pre className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2 max-h-24 overflow-y-auto whitespace-pre-wrap">
                  {v.contentSnapshot.slice(0, 500)}
                  {v.contentSnapshot.length > 500 ? "…" : ""}
                </pre>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          {restoreError && <p role="alert" className="text-sm text-destructive">{restoreError}</p>}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        {confirmVersion && <div className="border rounded p-3 space-y-3" role="group" aria-label="Confirm restoration">
          <p>Create or resume a revision draft on this original source from the selected snapshot? Current published content stays unchanged until approved publication. Normal review and approval are required. Publication creates the next same-lineage version (vN+1), not a duplicate source. Repeating this restoration resumes the active revision.</p>
          <Button disabled={restore.isPending} onClick={() => {
            if (submitting.current) return;
            submitting.current = true;
            restore.mutate(confirmVersion);
          }}>{restore.isPending ? "Restoring…" : "Confirm restore"}</Button>
          <Button variant="outline" disabled={restore.isPending} onClick={() => setConfirmVersion(undefined)}>Cancel</Button>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

// ─── Tab panel ────────────────────────────────────────────────────────────────

interface TabPanelProps {
  status: string;
  onAction: (id: string, action: string, comment?: string) => void;
  onViewHistory: (id: string) => void;
  onCreateRevision: (id: string) => void;
  creatingRevision: boolean;
  canReview: boolean;
  canPublish: boolean;
  canManage: boolean;
}

function TabPanel({ status, onAction, onViewHistory, onCreateRevision, creatingRevision, canReview, canPublish, canManage }: TabPanelProps) {
  const { data, isLoading, isError, error } = useQuery<GovernanceResponse>({
    queryKey: ["content-governance", status],
    queryFn: () =>
      apiRequest("GET", `/api/content-governance?status=${status}&pageSize=100`)
        .then(r => r.json()),
    refetchInterval: 15_000,
  });

  if (isLoading) {
    return (
      <div className="py-12 text-center text-sm text-gray-500">Loading…</div>
    );
  }
  if (isError) {
    const msg = error instanceof Error ? error.message : "Failed to load content.";
    return (
      <div className="py-12 text-center text-sm text-red-600">
        {msg} <button className="underline ml-1" onClick={() => window.location.reload()}>Refresh</button>
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="py-12 text-center">
        <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          No {STATUS_CONFIG[status]?.label.toLowerCase() ?? status} content.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(item => (
        <SourceRow
          key={item.id}
          item={item}
          onAction={onAction}
          onViewHistory={onViewHistory}
          onCreateRevision={onCreateRevision}
          creatingRevision={creatingRevision}
          canReview={canReview}
          canPublish={canPublish}
          canManage={canManage}
        />
      ))}
      {data && data.total > items.length && (
        <p className="text-xs text-gray-400 text-center pt-2">
          Showing {items.length} of {data.total} items
        </p>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ActiveRevisions({ onOpen }: { onOpen: (id: string) => void }) {
  const query = useQuery<GovernanceRevision[]>({
    queryKey: ["content-governance-revisions"],
    queryFn: async () => (await apiRequest("GET", "/api/content-governance/revisions")).json(),
    refetchInterval: 15_000,
  });
  return <Card className="mb-6">
    <CardHeader className="pb-3">
      <CardTitle className="text-base">Active revisions</CardTitle>
      <p className="text-sm text-muted-foreground">Same-source changes awaiting publication. The current published content remains live.</p>
    </CardHeader>
    <CardContent className="space-y-2">
      {query.isLoading && <div role="status" aria-label="Loading active revisions" className="animate-pulse h-20 rounded bg-muted" />}
      {query.error && <div role="alert" className="text-sm text-destructive">
        <p>{governanceErrorMessage(query.error)}</p>
        <Button variant="outline" size="sm" disabled={query.isFetching} onClick={() => void query.refetch()}>Retry</Button>
      </div>}
      {!query.isLoading && !query.error && !query.data?.length && <div className="rounded-lg border border-dashed p-5 text-sm text-muted-foreground">
        <p className="font-medium">No active revisions.</p>
        <p className="mt-1">Create a revision from eligible published plain text, or restore a snapshot from History.</p>
      </div>}
      {query.data?.map(revision => <div key={revision.id} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-gray-200 p-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><span className="font-medium text-sm break-words">{revision.name}</span><GovernanceBadge status={revision.status} /></div>
          <p className="mt-1 text-xs text-muted-foreground break-words">
            Original source {revision.knowledgeSourceId} · Published v{revision.currentPublishedVersion} · {revision.restoredFromVersion !== null ? `Restored from v${revision.restoredFromVersion}` : `Based on v${revision.baseVersion}`}
          </p>
          <p className="mt-1 text-xs text-muted-foreground break-words">Created by {revision.createdByUserId} · {fmt(revision.createdAt)}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => onOpen(revision.id)}>Open / resume revision</Button>
      </div>)}
    </CardContent>
  </Card>;
}

export default function ContentGovernancePage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [historySourceId, setHistorySourceId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState(() => new URLSearchParams(window.location.search).get("draft"));
  const [revisionId, setRevisionId] = useState(() => new URLSearchParams(window.location.search).get("revision"));
  const [publishedRevision, setPublishedRevision] = useState<GovernanceRevision>();
  const creating = useRef(false);
  function openRevision(id: string) {
    setDraftId(null); setRevisionId(id); setPublishedRevision(undefined);
    const params = new URLSearchParams(window.location.search);
    params.delete("draft"); params.set("revision", id);
    window.history.replaceState(null, "", `/content-governance?${params}`);
  }
  function closeRevision() {
    setRevisionId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("revision");
    window.history.replaceState(null, "", `/content-governance${params.size ? `?${params}` : ""}`);
  }

  // Permission flags read from the JWT claims cached by useAuth().
  // These drive button visibility only — the server enforces the same
  // gates via [HasPermission] and returns 403 for any bypass attempt.
  const { user } = useAuth();
  const canManage  = hasPermission(user, "knowledge.manage");
  const canReview  = hasPermission(user, "content.review");
  const canPublish = hasPermission(user, "content.publish");

  // Summary counts
  const { data: summary } = useQuery<{ status: string; count: number }[]>({
    queryKey: ["content-governance-summary"],
    queryFn: () => apiRequest("GET", "/api/content-governance/summary").then(r => r.json()),
    refetchInterval: 30_000,
  });

  const countFor = (status: string) =>
    summary?.find(s => s.status === status)?.count ?? 0;

  // ── Mutations ──

  const createRevision = useMutation({
    mutationFn: async (sourceId: string): Promise<GovernanceRevision> =>
      (await apiRequest("POST", `/api/content-governance/${sourceId}/revisions`)).json(),
    onSuccess: (revision) => {
      qc.invalidateQueries({ queryKey: ["content-governance"] });
      qc.invalidateQueries({ queryKey: ["content-governance-summary"] });
      qc.invalidateQueries({ queryKey: ["content-governance-revisions"] });
      qc.invalidateQueries({ queryKey: ["content-governance-revision", revision.id] });
      openRevision(revision.id);
    },
    onError: (err: unknown) => toast({ title: "Unable to create revision", description: governanceErrorMessage(err), variant: "destructive" }),
    onSettled: () => { creating.current = false; },
  });
  function handleCreateRevision(sourceId: string) {
    if (creating.current || !canManage) return;
    creating.current = true;
    createRevision.mutate(sourceId);
  }

  const doAction = useMutation({
    mutationFn: ({ id, action, comment }: { id: string; action: string; comment?: string }) => {
      const body = comment ? JSON.stringify({ comment }) : undefined;
      return apiRequest(
        "POST",
        `/api/content-governance/${id}/${action}`,
        body ? JSON.parse(body) : undefined
      );
    },
    onSuccess: (_data, vars) => {
      const labels: Record<string, string> = {
        submit: "Submitted for review",
        approve: "Content approved",
        reject: "Content rejected",
        publish: "Content published — now live in RAG retrieval",
        archive: "Content archived",
      };
      toast({ title: labels[vars.action] ?? "Done" });
      // Invalidate all governance tab queries + summary
      qc.invalidateQueries({ queryKey: ["content-governance"] });
      qc.invalidateQueries({ queryKey: ["content-governance-summary"] });
    },
    onError: (err: unknown) => {
      toast({ title: "Error", description: governanceErrorMessage(err), variant: "destructive" });
    },
  });

  const handleAction = (id: string, action: string, comment?: string) => {
    doAction.mutate({ id, action, comment });
  };

  const TABS = [
    { value: "draft",     label: "Draft",          status: "draft" },
    { value: "submitted", label: "Pending Review",  status: "submitted" },
    { value: "approved",  label: "Approved",        status: "approved" },
    { value: "rejected",  label: "Rejected",        status: "rejected" },
    { value: "published", label: "Published",       status: "published" },
    { value: "archived",  label: "Archived",        status: "archived" },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Content Governance</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage the knowledge lifecycle: Draft → Review → Approval → Published.
          Only <strong>Published</strong> content reaches chatbot RAG retrieval.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {TABS.map(t => {
          const cfg = STATUS_CONFIG[t.status];
          const count = countFor(t.status);
          return (
            <Card key={t.value} className={`border ${count > 0 && t.status === "submitted" ? "border-yellow-300 shadow-sm" : "border-gray-200"}`}>
              <CardContent className="p-3 text-center">
                <div className={`inline-flex items-center justify-center w-6 h-6 rounded-full mb-1 ${cfg.color}`}>
                  {cfg.icon}
                </div>
                <div className="text-xl font-bold text-gray-900">{count}</div>
                <div className="text-xs text-gray-500">{cfg.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Workflow legend */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="font-medium text-gray-700">Workflow:</span>
        {["draft", "submitted", "approved", "published"].map((s, i, arr) => (
          <span key={s} className="flex items-center gap-1">
            <GovernanceBadge status={s} />
            {i < arr.length - 1 && <ChevronRight className="h-3 w-3 text-gray-400" />}
          </span>
        ))}
        <span className="text-gray-400 ml-2">or</span>
        <GovernanceBadge status="rejected" />
        <span className="text-gray-400">/</span>
        <GovernanceBadge status="archived" />
      </div>

      {/* Tabs */}
      {publishedRevision && <div role="status" className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
        <p>Published v{publishedRevision.publishedVersion} on original source <strong>{publishedRevision.name}</strong> ({publishedRevision.knowledgeSourceId}). No duplicate source was created.</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => setHistorySourceId(publishedRevision.knowledgeSourceId)}>View original source history</Button>
      </div>}
      {(canManage || canReview || canPublish) && <ActiveRevisions onOpen={openRevision} />}
      <Tabs defaultValue="submitted">
        <TabsList className="mb-4 flex-wrap h-auto gap-1">
          {TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="relative">
              {t.label}
              {countFor(t.status) > 0 && (
                <span className="ml-1.5 rounded-full bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                  {countFor(t.status)}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map(t => (
          <TabsContent key={t.value} value={t.value}>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  {STATUS_CONFIG[t.status]?.icon}
                  {t.label} Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TabPanel
                  status={t.status}
                  onAction={handleAction}
                  onViewHistory={(id) => setHistorySourceId(id)}
                  onCreateRevision={handleCreateRevision}
                  creatingRevision={createRevision.isPending}
                  canReview={canReview}
                  canPublish={canPublish}
                  canManage={canManage}
                />
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {revisionId && (canManage || canReview || canPublish) && <RevisionEditor key={revisionId} id={revisionId} onClose={closeRevision} onPublished={revision => {
        closeRevision();
        setPublishedRevision(revision);
        setHistorySourceId(revision.knowledgeSourceId);
      }} />}
      {!revisionId && draftId && canManage && <DraftEditor id={draftId} onClose={() => {
        setDraftId(null);
        window.history.replaceState(null, "", "/content-governance");
      }} />}
      {/* Version history modal */}
      {historySourceId && (
        <VersionHistoryPanel
          sourceId={historySourceId}
          onClose={() => setHistorySourceId(null)}
        />
      )}
    </div>
  );
}
